import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, CheckCircle, Home, Building, Palette, ArrowRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
const Services = () => {
  const handleContactClick = () => {
    window.location.href = "tel:0658864706";
  };
  const handleDevisClick = () => {
    window.location.href = "/contact";
  };
  const services = [{
    title: "Peinture Intérieure",
    description: "Transformation complète de vos espaces intérieurs avec des finitions professionnelles",
    icon: Home,
    features: ["Peinture des murs et plafonds", "Préparation des surfaces", "Pose de papier peint", "Finitions décoratives", "Conseils couleurs et matériaux"],
    image: "./images/Interieur 2.jpeg"
  }, {
    title: "Peinture Extérieure & Ravalement",
    description: "Protection et embellissement de vos façades pour améliorer l'isolation et les performances",
    icon: Building,
    features: ["Ravalement de façades", "Peinture extérieure", "Traitement anti-mousse", "Réparation des fissures", "Amélioration de l'isolation"],
    image: "./images/Exterieur .png"
  }, {
    title: "Plafonds & Revêtements",
    description: "Installation de plafonds, faux plafonds et revêtements muraux et de sols",
    icon: Palette,
    features: ["Pose de faux plafonds", "Plafonds tendus", "Revêtements muraux", "Revêtements de sols", "Application de crépis"],
    image: "./images/Interieur 1.png"
  }];
  const processSteps = [{
    number: "1",
    title: "Consultation gratuite",
    description: "Visite sur site et analyse de vos besoins"
  }, {
    number: "2",
    title: "Devis détaillé",
    description: "Proposition personnalisée avec tarifs transparents"
  }, {
    number: "3",
    title: "Planification",
    description: "Organisation des travaux selon vos disponibilités"
  }, {
    number: "4",
    title: "Réalisation",
    description: "Exécution soignée avec matériaux de qualité"
  }];
  const guarantees = ["Garantie qualité", "Respect des délais", "Matériaux premium"];
  return <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary-glow to-secondary py-20 lg:py-32">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-white">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              Services professionnels
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Nos Services
            </h1>
            <p className="text-xl mb-8 text-white/90 leading-relaxed max-w-3xl mx-auto">
              Découvrez notre gamme complète de services de peinture et décoration. 
              De la consultation à la réalisation, nous vous accompagnons dans tous vos projets.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
            const IconComponent = service.icon;
            return <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="bg-primary text-primary-foreground p-3 rounded-lg">
                        <IconComponent className="h-8 w-8" />
                      </div>
                      <Badge variant="secondary">{`0${index + 1}`}</Badge>
                    </div>
                    <h2 className="text-3xl font-bold mb-4">{service.title}</h2>
                    <p className="text-lg text-muted-foreground mb-6">{service.description}</p>
                    
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, featureIndex) => <li key={featureIndex} className="flex items-center space-x-3">
                          <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                    
                    <Button onClick={handleDevisClick} className="bg-primary hover:bg-primary/90">
                      Demander un devis pour ce service
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                  
                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="rounded-lg shadow-xl w-full h-[400px] object-cover"
                    />
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Notre Processus</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Une méthode éprouvée pour garantir la réussite de vos projets
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.number}
                  </div>
                  <CardTitle className="text-xl">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{step.description}</CardDescription>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Nos Garanties</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Votre satisfaction est notre priorité
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {guarantees.map((guarantee, index) => <div key={index} className="text-center">
                <div className="w-20 h-20 bg-accent text-accent-foreground rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{guarantee}</h3>
                <p className="text-muted-foreground">
                  {index === 0 && "Tous nos travaux sont garantis 2 ans"}
                  {index === 1 && "Engagement sur les dates de livraison"}
                  {index === 2 && "Utilisation de peintures et matériaux haut de gamme"}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Prêt à démarrer votre projet ?</h2>
          <p className="text-xl mb-8 opacity-90">
            Contactez-nous dès maintenant pour une consultation gratuite et un devis personnalisé.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={handleContactClick} size="lg" className="bg-secondary hover:bg-secondary/90 text-white px-8 py-4">
              <Phone className="h-5 w-5 mr-2" />
              06 58 86 47 06
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary px-8 py-4">
              <Link to="/contact">
                Demander un devis
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
};
export default Services;